import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home/Home';
import Room from './pages/Room/Room';
import RoomList from './pages/RoomList/RoomList';
import Login from './components/Login/Login';
import Layout from './components/Layout/Layout';
import axios from 'axios';
import Landing from './pages/Landing/Landing';
import ContactSales from './pages/ContactSales/ContactSales';
import { useTranslation } from 'react-i18next';
import organizationService from './services/organizationService';
import userService from './services/userService';

axios.defaults.baseURL = process.env.REACT_APP_BASE_DOMAIN;
axios.defaults.withCredentials = true;

// Attach request interceptor to include token
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Removed AppContent; routes now defined directly with auth guard

const App = () => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [currentOrganization, setCurrentOrganization] = React.useState(null);
  const [currentResourceGroup, setCurrentResourceGroup] = React.useState(null);
  const [resourceGroups, setResourceGroups] = React.useState([]);
  const [isResourceGroupsLoading, setIsResourceGroupsLoading] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState(null);

  const clearAuthState = React.useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    userService.clear();
    organizationService.clear();
    setCurrentUser(null);
    setCurrentOrganization(null);
    setCurrentResourceGroup(null);
    setResourceGroups([]);
    setIsResourceGroupsLoading(false);
  }, []);

  const initializeResourceGroups = React.useCallback(async () => {
    setIsResourceGroupsLoading(true);
    try {
      const groups = await organizationService.getResourceGroups(null);
      setResourceGroups(groups);
      const storedGroupId = localStorage.getItem('currentResourceGroupId');
      let selected = null;
      if (storedGroupId && groups.length > 0) {
        const stored = groups.find(g => g.id === parseInt(storedGroupId));
        selected = stored || groups[0];
      } else if (groups.length > 0) {
        selected = groups[0];
      }
      if (selected) {
        setCurrentResourceGroup(selected);
        localStorage.setItem('currentResourceGroupId', selected.id.toString());
      } else {
        setCurrentResourceGroup(null);
      }
    } catch (_) {
      setResourceGroups([]);
      setCurrentResourceGroup(null);
    } finally {
      setIsResourceGroupsLoading(false);
    }
  }, []);

  const initializeServices = React.useCallback(async () => {
    try {
      const user = await userService.initialize();
      if (!user) {
        clearAuthState();
        return false;
      }
      setCurrentUser(user);
      const org = await organizationService.initialize();
      setCurrentOrganization(org);
      await initializeResourceGroups();
      return true;
    } catch (error) {
      clearAuthState();
      return false;
    }
  }, [clearAuthState, initializeResourceGroups]);

  React.useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Token ${token}`;
        const ok = await initializeServices();
        if (!cancelled) {
          setIsAuthenticated(ok);
          setIsLoading(false);
        }
        return;
      }
      try {
        const response = await axios.get('/api/token/');
        if (response.data?.token) {
          localStorage.setItem('token', response.data.token);
          axios.defaults.headers.common['Authorization'] = `Token ${response.data.token}`;
          const ok = await initializeServices();
          if (!cancelled) {
            setIsAuthenticated(ok);
          }
        } else if (!cancelled) {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error checking token:', error);
        if (!cancelled) {
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [initializeServices]);

  React.useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          clearAuthState();
          setIsAuthenticated(false);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [clearAuthState]);

  const handleLoginSuccess = () => {
    initializeServices().then((ok) => {
      setIsAuthenticated(ok);
    });
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/signout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthState();
      setIsAuthenticated(false);
    }
  };

  const handleResourceGroupChange = (resourceGroup) => {
    setCurrentResourceGroup(resourceGroup);
  };

  // While we are checking the server for an existing token, show a loading indicator
  if (isLoading) {
    return <div>{t('loading')}</div>;
  }

  const RequireAuth = ({ children }) => (
    isAuthenticated ? children : <Navigate to="/login" replace />
  );

  return (
    <Router>
      <Layout
        handleLogout={isAuthenticated ? handleLogout : undefined}
        currentOrganization={currentOrganization}
        currentResourceGroup={currentResourceGroup}
        onResourceGroupChange={handleResourceGroupChange}
        resourceGroups={resourceGroups}
        isResourceGroupsLoading={isResourceGroupsLoading}
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
      >
        <Routes>
          <Route path="/" element={isAuthenticated ? <RequireAuth><Home key={currentOrganization?.id} currentUser={currentUser} currentResourceGroup={currentResourceGroup} /></RequireAuth> : <Landing />} />
          <Route path="/login" element={isAuthenticated ? <Navigate to="/home" replace /> : <Login onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/contact-us" element={<ContactSales />} />
          <Route path="/rooms" element={<RequireAuth><RoomList key={currentOrganization?.id} /></RequireAuth>} />
          <Route path="/room/:roomId" element={<RequireAuth><Room key={currentOrganization?.id} /></RequireAuth>} />
          <Route path="/home" element={<RequireAuth><Home key={currentOrganization?.id} currentUser={currentUser} currentResourceGroup={currentResourceGroup} /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </Router>
  );
};

export default App;
